import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { 
  RefreshCw, AlertCircle, CheckCircle, Camera, 
  Check, X, Star, Trash2, Edit, Archive, Plus, Upload, Image, Loader2
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import api from '../../services/api';

const GalleryTab = ({
  galleryLoading,
  pendingPhotos,
  approvedPhotos,
  handleApprovePhoto,
  handleRejectPhoto,
  handleToggleFeatured,
  fetchGalleryPhotos
}) => {
  const [viewMode, setViewMode] = useState('all'); // 'all', 'before', 'after', 'paired'
  const [editModal, setEditModal] = useState({ open: false, photo: null });
  const [editData, setEditData] = useState({});
  const [uploadModal, setUploadModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [uploadData, setUploadData] = useState({
    photo_type: 'before',
    room: '',
    caption: '',
    photo_url: '',
    selectedFile: null
  });

  const handleArchivePhoto = async (photoId) => {
    if (!window.confirm('Archive this photo?')) return;
    try {
      await api.post(`/gallery/${photoId}/archive`);
      toast.success('Photo archived');
      if (fetchGalleryPhotos) fetchGalleryPhotos();
    } catch (error) {
      toast.error('Failed to archive photo');
    }
  };

  const handleDeletePhoto = async (photoId) => {
    if (!window.confirm('Delete this photo permanently?')) return;
    try {
      await api.delete(`/gallery/${photoId}`);
      toast.success('Photo deleted');
      if (fetchGalleryPhotos) fetchGalleryPhotos();
    } catch (error) {
      toast.error('Failed to delete photo');
    }
  };

  const handleOpenEditModal = (photo) => {
    setEditData({
      caption: photo.caption || '',
      room: photo.room || '',
      photo_type: photo.photo_type || 'before',
      is_featured: photo.is_featured || false
    });
    setEditModal({ open: true, photo });
  };

  const handleSaveEdit = async () => {
    try {
      await api.put(`/gallery/${editModal.photo.id}`, editData);
      toast.success('Photo updated');
      setEditModal({ open: false, photo: null });
      if (fetchGalleryPhotos) fetchGalleryPhotos();
    } catch (error) {
      toast.error('Failed to update photo');
    }
  };

  const handleUploadPhoto = async (e) => {
    e.preventDefault();
    
    // If a file is selected, upload it first
    if (uploadData.selectedFile) {
      setUploading(true);
      try {
        const formData = new FormData();
        formData.append('file', uploadData.selectedFile);
        formData.append('category', 'photo');
        formData.append('destination', 'gallery');
        formData.append('label', `${uploadData.photo_type} Photo`);
        formData.append('description', uploadData.caption || `${uploadData.photo_type} photo - ${uploadData.room}`);

        const uploadResponse = await api.post('/uploads/universal', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        // Now save to gallery with the uploaded file URL
        await api.post('/gallery/upload', {
          photo_type: uploadData.photo_type,
          room: uploadData.room,
          caption: uploadData.caption,
          photo_url: uploadResponse.data.file_url
        });
        
        toast.success('Photo uploaded successfully!');
        setUploadData({ photo_type: 'before', room: '', caption: '', photo_url: '', selectedFile: null });
        setUploadModal(false);
        if (fetchGalleryPhotos) fetchGalleryPhotos();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to upload photo');
      } finally {
        setUploading(false);
      }
    } else if (uploadData.photo_url.trim()) {
      // Fallback to URL if provided
      try {
        await api.post('/gallery/upload', uploadData);
        toast.success('Photo uploaded successfully!');
        setUploadData({ photo_type: 'before', room: '', caption: '', photo_url: '', selectedFile: null });
        setUploadModal(false);
        if (fetchGalleryPhotos) fetchGalleryPhotos();
      } catch (error) {
        toast.error('Failed to upload photo');
      }
    } else {
      toast.error('Please select a photo to upload');
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please select a valid image (JPEG, PNG, WebP, or GIF)');
      return;
    }

    // Validate file size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image must be less than 10MB');
      return;
    }

    setUploadData(prev => ({ ...prev, selectedFile: file }));
  };

  // Group photos by before/after pairs (same job or room)
  const groupPhotosByPairs = () => {
    const beforePhotos = approvedPhotos.filter(p => p.photo_type === 'before');
    const afterPhotos = approvedPhotos.filter(p => p.photo_type === 'after');
    return { beforePhotos, afterPhotos };
  };

  const filteredPhotos = () => {
    if (viewMode === 'before') return approvedPhotos.filter(p => p.photo_type === 'before');
    if (viewMode === 'after') return approvedPhotos.filter(p => p.photo_type === 'after');
    return approvedPhotos;
  };

  const { beforePhotos, afterPhotos } = groupPhotosByPairs();

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold font-['Cormorant_Garamond']">Photo Gallery</h1>
          <p className="text-[var(--text-muted)] mt-1">Before & After photos from completed jobs</p>
        </div>
        <Button onClick={() => setUploadModal(true)} className="btn-primary">
          <Plus className="w-4 h-4 mr-2" />
          Add Photo
        </Button>
      </div>

      {/* Upload Photo Modal */}
      {uploadModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setUploadModal(false)}>
          <div className="bg-[var(--background-paper)] rounded-xl p-6 w-full max-w-lg" onClick={e => e.stopPropagation()}>
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
              <Upload className="w-5 h-5 text-[#D4AF37]" />
              Add Photo
            </h2>
            <form onSubmit={handleUploadPhoto} className="space-y-4">
              {/* File Upload */}
              <div>
                <Label>Upload Photo *</Label>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileSelect}
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  className="hidden"
                />
                <div 
                  onClick={() => fileInputRef.current?.click()}
                  className="mt-2 border-2 border-dashed border-[var(--border)] rounded-lg p-6 text-center cursor-pointer hover:border-[var(--primary)] transition-colors"
                >
                  {uploadData.selectedFile ? (
                    <div className="space-y-2">
                      <CheckCircle className="w-8 h-8 mx-auto text-green-500" />
                      <p className="text-sm font-medium">{uploadData.selectedFile.name}</p>
                      <p className="text-xs text-[var(--text-muted)]">Click to change</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Camera className="w-8 h-8 mx-auto text-[var(--text-muted)]" />
                      <p className="text-sm text-[var(--text-secondary)]">Click to select a photo</p>
                      <p className="text-xs text-[var(--text-muted)]">JPEG, PNG, WebP or GIF (max 10MB)</p>
                    </div>
                  )}
                </div>
              </div>
              <div>
                <Label>Photo Type *</Label>
                <Select value={uploadData.photo_type} onValueChange={(v) => setUploadData({ ...uploadData, photo_type: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="before">Before</SelectItem>
                    <SelectItem value="after">After</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Room/Area</Label>
                <Input
                  value={uploadData.room}
                  onChange={(e) => setUploadData({ ...uploadData, room: e.target.value })}
                  placeholder="Kitchen, Bathroom, Living Room..."
                />
              </div>
              <div>
                <Label>Caption</Label>
                <Textarea
                  value={uploadData.caption}
                  onChange={(e) => setUploadData({ ...uploadData, caption: e.target.value })}
                  placeholder="Description of the photo..."
                  rows={2}
                />
              </div>
              <div className="flex gap-2">
                <Button type="submit" className="flex-1 btn-primary" disabled={uploading}>
                  {uploading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    'Upload Photo'
                  )}
                </Button>
                <Button type="button" variant="outline" onClick={() => setUploadModal(false)} disabled={uploading}>Cancel</Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Photo Modal */}
      {editModal.open && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setEditModal({ open: false, photo: null })}>
          <div className="bg-[var(--background-paper)] rounded-xl p-6 w-full max-w-lg" onClick={e => e.stopPropagation()}>
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
              <Edit className="w-5 h-5 text-[#D4AF37]" />
              Edit Photo
            </h2>
            {editModal.photo?.photo_url && (
              <img src={editModal.photo.photo_url} alt="Preview" className="w-full h-48 object-cover rounded-lg mb-4" />
            )}
            <div className="space-y-4">
              <div>
                <Label>Photo Type</Label>
                <Select value={editData.photo_type} onValueChange={(v) => setEditData({ ...editData, photo_type: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="before">Before</SelectItem>
                    <SelectItem value="after">After</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Room/Area</Label>
                <Input
                  value={editData.room}
                  onChange={(e) => setEditData({ ...editData, room: e.target.value })}
                />
              </div>
              <div>
                <Label>Caption</Label>
                <Textarea
                  value={editData.caption}
                  onChange={(e) => setEditData({ ...editData, caption: e.target.value })}
                  rows={2}
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_featured"
                  checked={editData.is_featured}
                  onChange={(e) => setEditData({ ...editData, is_featured: e.target.checked })}
                  className="w-4 h-4"
                />
                <label htmlFor="is_featured" className="text-sm">Featured Photo</label>
              </div>
              <div className="flex gap-2">
                <Button onClick={handleSaveEdit} className="flex-1 btn-primary">Save Changes</Button>
                <Button variant="outline" onClick={() => setEditModal({ open: false, photo: null })}>Cancel</Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Mode Toggle */}
      <div className="flex gap-2 mb-6">
        <Button
          variant={viewMode === 'all' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setViewMode('all')}
        >
          All Photos
        </Button>
        <Button
          variant={viewMode === 'before' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setViewMode('before')}
          className={viewMode === 'before' ? 'bg-amber-500 hover:bg-amber-600' : ''}
        >
          Before ({beforePhotos.length})
        </Button>
        <Button
          variant={viewMode === 'after' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setViewMode('after')}
          className={viewMode === 'after' ? 'bg-emerald-500 hover:bg-emerald-600' : ''}
        >
          After ({afterPhotos.length})
        </Button>
        <Button
          variant={viewMode === 'paired' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setViewMode('paired')}
        >
          Side by Side
        </Button>
      </div>

      {galleryLoading ? (
        <div className="card p-12 text-center">
          <RefreshCw className="w-8 h-8 animate-spin text-[var(--primary)] mx-auto mb-4" />
          <p className="text-[var(--text-muted)]">Loading photos...</p>
        </div>
      ) : (
        <>
          {/* Pending Photos Section */}
          {pendingPhotos.length > 0 && (
            <div className="mb-8">
              <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-orange-500" />
                Pending Approval ({pendingPhotos.length})
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {pendingPhotos.map(photo => (
                  <div key={photo.id} className="card overflow-hidden">
                    <div className="aspect-video bg-[var(--background-subtle)] relative">
                      <img 
                        src={photo.photo_url} 
                        alt={photo.caption || 'Gallery photo'} 
                        className="w-full h-full object-cover"
                      />
                      <span className={`absolute top-2 left-2 px-2 py-1 rounded text-xs font-bold text-white ${photo.photo_type === 'before' ? 'bg-amber-500' : 'bg-emerald-500'}`}>
                        {photo.photo_type === 'before' ? 'BEFORE' : 'AFTER'}
                      </span>
                    </div>
                    <div className="p-4">
                      <p className="font-semibold">{photo.worker_name}</p>
                      <p className="text-sm text-[var(--text-muted)]">{photo.room || 'General'}</p>
                      {photo.caption && <p className="text-sm mt-2">{photo.caption}</p>}
                      <p className="text-xs text-[var(--text-muted)] mt-2">
                        {new Date(photo.created_at).toLocaleDateString()}
                      </p>
                      <div className="flex gap-2 mt-4">
                        <Button size="sm" onClick={() => handleApprovePhoto(photo.id, false)} className="btn-primary flex-1">
                          <Check className="w-4 h-4 mr-1" /> Approve
                        </Button>
                        <Button size="sm" onClick={() => handleApprovePhoto(photo.id, true)} className="btn-secondary" title="Approve and Feature">
                          <Star className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleRejectPhoto(photo.id)} className="text-[var(--error)]">
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Side by Side View */}
          {viewMode === 'paired' && (
            <div className="mb-8">
              <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                <Image className="w-5 h-5 text-[#D4AF37]" />
                Before & After Comparison
              </h2>
              <div className="space-y-6">
                {beforePhotos.map((beforePhoto, idx) => {
                  const afterPhoto = afterPhotos[idx];
                  return (
                    <div key={beforePhoto.id} className="card p-4">
                      <div className="grid md:grid-cols-2 gap-4">
                        {/* Before */}
                        <div>
                          <div className="aspect-video bg-[var(--background-subtle)] relative rounded-lg overflow-hidden">
                            <img src={beforePhoto.photo_url} alt="Before" className="w-full h-full object-cover" />
                            <span className="absolute top-2 left-2 px-2 py-1 rounded text-xs font-bold text-white bg-amber-500">BEFORE</span>
                          </div>
                          <p className="text-sm text-[var(--text-secondary)] mt-2">{beforePhoto.room || 'General'}</p>
                        </div>
                        {/* After */}
                        <div>
                          {afterPhoto ? (
                            <>
                              <div className="aspect-video bg-[var(--background-subtle)] relative rounded-lg overflow-hidden">
                                <img src={afterPhoto.photo_url} alt="After" className="w-full h-full object-cover" />
                                <span className="absolute top-2 left-2 px-2 py-1 rounded text-xs font-bold text-white bg-emerald-500">AFTER</span>
                              </div>
                              <p className="text-sm text-[var(--text-secondary)] mt-2">{afterPhoto.room || 'General'}</p>
                            </>
                          ) : (
                            <div className="aspect-video bg-[var(--background-subtle)] rounded-lg flex items-center justify-center">
                              <p className="text-gray-400">No after photo</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Regular Grid View */}
          {viewMode !== 'paired' && (
            <div>
              <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-[var(--success)]" />
                {viewMode === 'before' ? 'Before Photos' : viewMode === 'after' ? 'After Photos' : 'All Approved Photos'} ({filteredPhotos().length})
              </h2>
              {filteredPhotos().length === 0 ? (
                <div className="card p-12 text-center">
                  <Camera className="w-16 h-16 text-[var(--text-muted)] mx-auto mb-4" />
                  <h3 className="text-lg font-bold mb-2">No Photos Yet</h3>
                  <p className="text-[var(--text-secondary)]">
                    Upload before/after photos from completed jobs to showcase your work.
                  </p>
                </div>
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {filteredPhotos().map(photo => (
                    <div key={photo.id} className="card overflow-hidden relative group">
                      <div className="aspect-video bg-[var(--background-subtle)]">
                        <img 
                          src={photo.photo_url} 
                          alt={photo.caption || 'Gallery photo'} 
                          className="w-full h-full object-cover"
                        />
                      </div>
                      {/* Hover Actions */}
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                        <Button 
                          size="icon" 
                          variant="ghost" 
                          className="text-white hover:bg-[var(--background-paper)]/20"
                          onClick={() => handleOpenEditModal(photo)}
                          title="Edit"
                        >
                          <Edit className="w-5 h-5" />
                        </Button>
                        <Button 
                          size="icon" 
                          variant="ghost" 
                          className="text-white hover:bg-[var(--background-paper)]/20"
                          onClick={() => handleToggleFeatured(photo.id)}
                          title={photo.is_featured ? 'Unfeature' : 'Feature'}
                        >
                          <Star className={`w-5 h-5 ${photo.is_featured ? 'fill-yellow-400 text-yellow-400' : ''}`} />
                        </Button>
                        <Button 
                          size="icon" 
                          variant="ghost" 
                          className="text-white hover:bg-[var(--background-paper)]/20"
                          onClick={() => handleArchivePhoto(photo.id)}
                          title="Archive"
                        >
                          <Archive className="w-5 h-5" />
                        </Button>
                        <Button 
                          size="icon" 
                          variant="ghost" 
                          className="text-white hover:bg-[var(--background-paper)]/20"
                          onClick={() => handleDeletePhoto(photo.id)}
                          title="Delete"
                        >
                          <Trash2 className="w-5 h-5" />
                        </Button>
                      </div>
                      {/* Labels */}
                      {photo.is_featured && (
                        <div className="absolute top-2 right-2">
                          <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                        </div>
                      )}
                      <span className={`absolute top-2 left-2 px-2 py-1 rounded text-xs font-bold text-white ${photo.photo_type === 'before' ? 'bg-amber-500' : 'bg-emerald-500'}`}>
                        {photo.photo_type === 'before' ? 'BEFORE' : 'AFTER'}
                      </span>
                      {/* Photo Info */}
                      <div className="p-2">
                        <p className="text-sm font-medium truncate">{photo.room || 'General'}</p>
                        {photo.caption && <p className="text-xs text-[var(--text-muted)] truncate">{photo.caption}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </motion.div>
  );
};

export default GalleryTab;
